import React, { useState, useEffect, useRef } from 'react';
import { Save, X, ArrowUpCircle, ArrowDownCircle, DollarSign, FileText, RotateCcw, AlertCircle, Loader2, Paperclip, Upload } from 'lucide-react';
import financeService from '../services/finance';
import cabangService from '../services/cabang';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);
const KATEGORI = ['OPERASIONAL', 'GAJI', 'BELANJA', 'PENDAPATAN', 'INVESTASI', 'LAINNYA'];
const METODE = ['TUNAI', 'TRANSFER', 'KARTU', 'LAINNYA'];

const TransaksiForm = ({ onSubmit, initialData = null, onCancel }) => {
  const fileInputRef = useRef(null);
  const [lampiranFile, setLampiranFile] = useState(null);
  const [lampiranName, setLampiranName] = useState(initialData?.lampiran || '');
  const [formData, setFormData] = useState({
    tanggal: initialData?.tanggal?.split('T')[0] || new Date().toISOString().split('T')[0],
    tipeTransaksi: initialData?.tipeTransaksi || 'PENGELUARAN',
    akunId: initialData?.akun?._id || initialData?.akun?.id || '',
    jumlah: initialData?.jumlah || '',
    deskripsi: initialData?.deskripsi || '',
    cabangId: initialData?.cabang?._id || initialData?.cabang?.id || '',
    namaCabang: initialData?.cabang?.namaCabang || '',
    kategori: initialData?.kategori || 'OPERASIONAL',
    metodePembayaran: initialData?.metodePembayaran || 'TUNAI',
    referensi: initialData?.referensi || ''
  });
  const [akunList, setAkunList] = useState([]);
  const [cabangList, setCabangList] = useState([]);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [akunData, cabangData] = await Promise.all([
          financeService.getAkun({ isActive: 'true' }),
          cabangService.getCabang()
        ]);
        setAkunList(akunData.akun || []);
        setCabangList(cabangData.cabang || []);
      } catch (err) {}
    };
    fetchData();
  }, []);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLampiranFile(file);
      setLampiranName(file.name);
    }
  };

  const handleRemoveFile = () => {
    setLampiranFile(null);
    setLampiranName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.akunId || !formData.jumlah || !formData.deskripsi) {
      setFormError('Akun, jumlah, dan deskripsi wajib diisi');
      return;
    }

    setFormLoading(true);
    try {
      // Re-derive namaCabang from cabangList at submit time for consistency
      const selectedCabang = formData.cabangId ? cabangList.find(c => c._id === formData.cabangId) : null;
      const payload = {
        ...formData,
        jumlah: Number(formData.jumlah),
        cabangId: formData.cabangId || undefined,
        namaCabang: selectedCabang?.name || undefined,
      };
      if (lampiranFile) {
        payload.lampiran = lampiranFile;
      }
      console.log('[TransaksiForm] Submit payload:', { cabangId: payload.cabangId, namaCabang: payload.namaCabang, tipe: payload.tipeTransaksi });
      await onSubmit(payload, initialData);
    } catch (err) {
      setFormError(err.message || 'Terjadi kesalahan');
    } finally {
      setFormLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      tanggal: new Date().toISOString().split('T')[0],
      tipeTransaksi: 'PENGELUARAN',
      akunId: '',
      cabangId: '',
      namaCabang: '',
      jumlah: '',
      deskripsi: '',
      kategori: 'OPERASIONAL',
      metodePembayaran: 'TUNAI',
      referensi: ''
    });
    setLampiranFile(null);
    setLampiranName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFormError('');
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {initialData ? 'Edit Transaksi' : 'Add Transaksi'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {initialData ? 'Update transaksi pemasukan atau pengeluaran' : 'Catat transaksi pemasukan atau pengeluaran ke akun'}
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="ml-4 p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {formError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{formError}</p>
          </div>
        )}

        {/* Section: Informasi Transaksi */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <div className={`p-1.5 rounded-lg ${formData.tipeTransaksi === 'PEMASUKAN' ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                {formData.tipeTransaksi === 'PEMASUKAN' ? <ArrowUpCircle className="w-4 h-4" /> : <ArrowDownCircle className="w-4 h-4" />}
              </div>
              <h3 className="section-title">Informasi Transaksi</h3>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipe Transaksi <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-2.5">
                  <button type="button" onClick={() => setFormData(p => ({ ...p, tipeTransaksi: 'PEMASUKAN' }))}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-xs font-semibold transition-all duration-200 ${formData.tipeTransaksi === 'PEMASUKAN' ? 'border-green-500 bg-green-50 text-green-700 shadow-sm scale-[1.02]' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}>
                    <ArrowUpCircle className={`w-6 h-6 ${formData.tipeTransaksi === 'PEMASUKAN' ? 'text-green-500' : 'text-gray-300'}`} />
                    Pemasukan
                  </button>
                  <button type="button" onClick={() => setFormData(p => ({ ...p, tipeTransaksi: 'PENGELUARAN' }))}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-xs font-semibold transition-all duration-200 ${formData.tipeTransaksi === 'PENGELUARAN' ? 'border-red-500 bg-red-50 text-red-700 shadow-sm scale-[1.02]' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}>
                    <ArrowDownCircle className={`w-6 h-6 ${formData.tipeTransaksi === 'PENGELUARAN' ? 'text-red-500' : 'text-gray-300'}`} />
                    Pengeluaran
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="tanggal" className="block text-sm font-medium text-gray-700 mb-1.5">Tanggal <span className="text-red-500">*</span></label>
                <input type="date" id="tanggal" value={formData.tanggal} onChange={handleChange('tanggal')}
                  className="input-field w-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Section: Detail Keuangan */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <h3 className="section-title">Detail Keuangan</h3>
            </div>
          </div>
          <div className="card-body space-y-5">
            <div>
              <label htmlFor="akunId" className="block text-sm font-medium text-gray-700 mb-1.5">
                Akun <span className="text-red-500">*</span>
              </label>
              <select id="akunId" value={formData.akunId} onChange={handleChange('akunId')} className="select-field w-full">
                <option value="">— Pilih Akun —</option>
                {akunList.map(a => (
                  <option key={a._id} value={a._id}>{a.kodeAkun} — {a.namaAkun} ({formatRupiah(a.saldoSaatIni)})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cabangId" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Cabang
                </label>
                <select id="cabangId" value={formData.cabangId} onChange={(e) => {
                  const selected = cabangList.find(c => c._id === e.target.value);
                  setFormData(p => ({ ...p, cabangId: e.target.value, namaCabang: selected?.name || '' }));
                }} className="select-field w-full">
                  <option value="">— Pilih Cabang —</option>
                  {cabangList.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="jumlah" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Jumlah (Rp) <span className="text-red-500">*</span>
                </label>
                <input type="number" id="jumlah" value={formData.jumlah}
                  onChange={handleChange('jumlah')}
                  className="input-field w-full text-lg font-bold" placeholder="0" min="0" />
                {formData.jumlah && Number(formData.jumlah) > 0 && (
                  <p className={`mt-1.5 text-xs font-semibold ${formData.tipeTransaksi === 'PEMASUKAN' ? 'text-green-600' : 'text-red-600'}`}>
                    {formData.tipeTransaksi === 'PEMASUKAN' ? '➕ Masuk: ' : '➖ Keluar: '}
                    <span className="font-mono">{formatRupiah(Number(formData.jumlah))}</span>
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="kategori" className="block text-sm font-medium text-gray-700 mb-1.5">Kategori</label>
                <select id="kategori" value={formData.kategori} onChange={handleChange('kategori')} className="select-field w-full">
                  {KATEGORI.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Keterangan */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <h3 className="section-title">Keterangan</h3>
            </div>
          </div>
          <div className="card-body space-y-5">
            <div>
              <label htmlFor="deskripsi" className="block text-sm font-medium text-gray-700 mb-1.5">
                Deskripsi <span className="text-red-500">*</span>
              </label>
              <textarea id="deskripsi" value={formData.deskripsi} onChange={handleChange('deskripsi')} rows={3}
                className="input-field w-full resize-none" placeholder="Jelaskan transaksi ini..." />
            </div>

            {/* Attachment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Lampiran
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                  className="hidden"
                  id="transaksi-lampiran-input"
                />
                <label
                  htmlFor="transaksi-lampiran-input"
                  className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all text-sm font-medium text-gray-600 hover:text-blue-600"
                >
                  <Upload className="w-4 h-4" />
                  <span>Choose File</span>
                </label>
                {lampiranName ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <Paperclip className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-blue-700 truncate max-w-[200px]">{lampiranName}</span>
                    <button type="button" onClick={handleRemoveFile} className="text-red-400 hover:text-red-600 ml-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : initialData?.lampiran ? (
                  <a
                    href={`http://localhost:5000${initialData.lampiran}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all"
                  >
                    <Paperclip className="w-4 h-4" />
                    <span className="truncate max-w-[200px]">View current file</span>
                  </a>
                ) : null}
                <span className="text-xs text-gray-400">PDF, JPG, PNG, Excel, Word (max 10MB)</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="metodePembayaran" className="block text-sm font-medium text-gray-700 mb-1.5">Metode Pembayaran</label>
                <select id="metodePembayaran" value={formData.metodePembayaran} onChange={handleChange('metodePembayaran')} className="select-field w-full">
                  {METODE.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="referensi" className="block text-sm font-medium text-gray-700 mb-1.5">No. Referensi</label>
                <input type="text" id="referensi" value={formData.referensi} onChange={handleChange('referensi')}
                  className="input-field w-full" placeholder="Invoice / KW No." />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between items-center">
          <div>
            {onCancel && (
              <button type="button" onClick={onCancel} className="btn-secondary">
                <X className="w-4 h-4" />
                <span>Close</span>
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button type="button" onClick={handleClear} className="btn-secondary">
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button type="submit" disabled={formLoading} className="btn-primary">
              {formLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /><span>Menyimpan...</span></>
              ) : (
                <><Save className="w-4 h-4" /><span>{initialData ? 'Update Transaksi' : 'Simpan Transaksi'}</span></>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TransaksiForm;
